/**
 * Auth Orchestrator
 *
 * Centralized logout handler. Owns identity + session + cleanup.
 * Does NOT own UI semantics — layouts react to lifecycle state independently.
 *
 * Replaces: utils/logout.js and all inline logout implementations.
 *
 * Cross-tab deduplication:
 *   Each tab has a unique tabId. When tab A broadcasts AUTH_LOGOUT,
 *   tab B handles it. Tab B does NOT re-broadcast, preventing event storms.
 *
 * Hard logout architecture:
 *   Critical cleanup (lifecycle, broadcast, token removal) runs BEFORE redirect.
 *   Noncritical cleanup (sockets, caches, assistant) runs AFTER redirect fires.
 *   This prevents React from re-rendering during teardown.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * BABEL-ESLINT CONSTRAINT (CRA 4 / react-scripts 4.0.1)
 * See useSessionRestore.ts header for full explanation.
 * DO NOT use: `as const`, `(x as Type)`, `catch (e: Type)`
 * ──────────────────────────────────────────────────────────────────────────
 */

import {
  setAuthLifecycle,
  setAuthenticatedUser,
  clearAuthenticatedUser,
} from '../redux/dashboard/reducers/userSlice';
import { clearNotifications } from '../redux/dashboard/reducers/notificationSlice';
import { sessionManager } from './SessionManager';
import { cleanupAllSockets } from './socket.io';
import { clearFeatureFlagCache } from './featureFlags';
import broadcastChannel from './BroadcastChannel';
import {
  AUTH_STORAGE_KEYS,
  CLEANUP_STORAGE_KEYS,
  PREFIXED_KEYS_TO_CLEAR,
} from '../constants/authKeys';

// ─── Types ───────────────────────────────────────────────────────────────────

export type LogoutMode = 'hard' | 'soft';

export interface LogoutConfig {
  mode: LogoutMode;
  /** Only used for 'hard' mode. Defaults to REACT_APP_MARKETING_URL */
  redirectTo?: string;
  reason?: 'user_initiated' | 'session_expired' | 'account_action';
}

/** BroadcastChannel event contract — events only, never user objects */
export type AuthBroadcastEvent =
  | { type: 'AUTH_LOGOUT'; reason: string; originTabId: string }
  ;

// ─── Tab Identity (cross-tab deduplication) ──────────────────────────────────

const TAB_ID = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// ─── Env Validation ──────────────────────────────────────────────────────────

const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || '';
if (!MARKETING_URL) {
  console.error(`[authOrchestrator] FATAL: Missing required env var: REACT_APP_MARKETING_URL. Auth redirects will fail.`);
}

// ─── Core Logout ─────────────────────────────────────────────────────────────

/**
 * Execute the complete logout sequence.
 *
 * Architecture: Critical → Redirect → Noncritical (best-effort)
 *
 * CRITICAL (before redirect):
 *   - Auth lifecycle state transition
 *   - Cancel in-flight session restore
 *   - Cross-tab broadcast
 *   - Token/storage removal
 *   - Server-side invalidation (fire-and-forget with keepalive)
 *
 * NONCRITICAL (after redirect fires, best-effort):
 *   - Socket disconnection
 *   - Assistant state cleanup
 *   - Encryption key clearing
 *   - Feature flag cache
 *   - Redux state cleanup
 *
 * @param dispatch - Redux dispatch function
 * @param config   - Logout configuration
 * @param options  - Internal options (e.g., silent for cross-tab)
 */
export function logout(dispatch, config, options) {
  if (!options) options = {};
  const { mode, redirectTo, reason = 'user_initiated' } = config;
  const { silent = false } = options;

  // ═══════════════════════════════════════════════════════════════════════════
  // CRITICAL CLEANUP — Must complete before redirect
  // ═══════════════════════════════════════════════════════════════════════════

  // 1. Transition to LOGGING_OUT — guards show loader, not /login redirect
  dispatch(setAuthLifecycle('LOGGING_OUT'));

  // 2. Cancel any in-flight session restore — prevents late verifyToken
  //    response from overriding this logout
  sessionManager.cancelRestore();

  // 3. Cross-tab broadcast (only for locally-initiated logouts)
  //    silent: true when triggered by another tab — prevents event storms
  if (!silent) {
    try {
      broadcastChannel.postMessage({
        type: 'AUTH_LOGOUT',
        reason,
        originTabId: TAB_ID,
      });
    } catch (e) {
      // BroadcastChannel may not be supported
    }
  }

  // 4. Server-side invalidation (fire-and-forget BEFORE redirect)
  //    keepalive: true ensures the request completes even after navigation
  try {
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
    if (token) {
      const apiBase = process.env.REACT_APP_API_BASE_URL || '';
      fetch(`${apiBase}/users/logout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        keepalive: true,
      }).catch(function() {}); // Swallow — don't block logout on network
    }
  } catch (e) {
    // Swallow
  }

  // 5. Remove auth tokens from storage — prevents session restore on back-button
  try { localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN); } catch (e) {}
  try { localStorage.removeItem(AUTH_STORAGE_KEYS.USER_ID); } catch (e) {}
  try { localStorage.removeItem(AUTH_STORAGE_KEYS.USER_TYPE); } catch (e) {}
  try { localStorage.removeItem(AUTH_STORAGE_KEYS.USER_DATA); } catch (e) {}

  // 6. Clear remaining (non-auth) localStorage keys and prefixed keys immediately
  //    Ensures they are wiped on both hard and soft logout.
  CLEANUP_STORAGE_KEYS.forEach(function(key) {
    try { localStorage.removeItem(key); } catch (e) {}
  });
  clearPrefixedKeys();

  // ═══════════════════════════════════════════════════════════════════════════
  // MODE-DEPENDENT FINALIZATION
  // ═══════════════════════════════════════════════════════════════════════════

  if (mode === 'hard') {
    // HARD: Redirect immediately — browser will destroy the SPA.
    // Noncritical cleanup runs after redirect fires (best-effort).
    const target = redirectTo || MARKETING_URL || '/';
    const url = target.endsWith('/') ? target : `${target}/`;

    // Navigate NOW — this is the critical moment.
    // DO NOT execute any code after this line.
    // window.location.replace() does not stop JS execution — React will
    // continue processing dispatches synchronously before the browser
    // actually navigates. Any dispatch(clearAuthenticatedUser()) would
    // transition lifecycle to UNAUTHENTICATED → AuthGuard renders /login → flash.
    // The browser will destroy the entire SPA on navigation. No cleanup needed.
    window.location.replace(url);
    return; // Explicit return — nothing should execute after hard redirect

  } else {
    // SOFT: No navigation — layouts react to lifecycle change independently.
    // Noncritical cleanup runs synchronously since the SPA stays alive.
    performNoncriticalCleanup(dispatch);

    // Transition to final state — layouts see UNAUTHENTICATED
    dispatch(clearAuthenticatedUser());
  }
}

// ─── Noncritical Cleanup ─────────────────────────────────────────────────────

/**
 * Best-effort cleanup that can run after redirect fires.
 * For hard logout: may not complete (browser destroying SPA). That's OK.
 * For soft logout: runs synchronously before state transition.
 */
function performNoncriticalCleanup(dispatch) {
  // Clear remaining (non-auth) localStorage keys
  CLEANUP_STORAGE_KEYS.forEach(function(key) {
    try { localStorage.removeItem(key); } catch (e) {}
  });

  // Clear prefixed dynamic keys (career-chat-*, encryption keys, etc.)
  clearPrefixedKeys();

  // Clear assistant state
  try {
    var userId = localStorage.getItem(AUTH_STORAGE_KEYS.USER_ID);
    var assistantSecurity = require('./assistantSecurity');
    assistantSecurity.performAssistantLogout(userId, dispatch);
  } catch (e) {
    // Non-critical
  }

  // Clear encryption keys
  try {
    var chatEncryption = require('./encryption').default;
    chatEncryption.clearAllKeys();
  } catch (e) {
    // Non-critical
  }

  // Clear feature flag cache
  try {
    clearFeatureFlagCache();
  } catch (e) {
    // Non-critical
  }

  // Disconnect all WebSocket connections
  try {
    cleanupAllSockets();
  } catch (e) {
    // Non-critical
  }

  // Redux cleanup (for hard mode: may not execute, that's fine)
  try {
    dispatch(clearNotifications()); // Clear notification cache — prevent cross-user leakage on re-login
    dispatch(clearAuthenticatedUser());
  } catch (e) {
    // Non-critical
  }
}

// ─── Core Login ──────────────────────────────────────────────────────────────

/**
 * Centralized login handler.
 *
 * Performs the 3 core responsibilities that every login flow repeats:
 *   1. Persist credentials to localStorage (for session restore on next boot)
 *   2. Hydrate Redux with setAuthenticatedUser (single dispatch, sets AUTHENTICATED)
 *   3. Cancel any stale session restore that may be in flight
 *
 * What stays at the call site (NOT centralized):
 *   - Permission dispatches (userPermission, departmentPermission, etc.)
 *   - Flow step dispatches
 *   - Navigation (history.push) — varies per flow
 *   - UI feedback (snackbar, loading states)
 *   - broadcastChannel for legacy login tab-sync (not auto-hydrating)
 *
 * NOTE: Does NOT dispatch updateAuthToken separately — the token is already
 * in the setAuthenticatedUser payload. Single source of truth.
 *
 * @param dispatch - Redux dispatch function
 * @param config   - Login configuration
 */
export function login(dispatch, config) {
  var user = config.user;
  var token = config.token;
  var userType = config.userType;

  // 1. Cancel any in-flight session restore — prevent race with login
  sessionManager.cancelRestore();

  // 2. Persist credentials for session restore on next boot
  try {
    if (user._id || user.id) {
      localStorage.setItem(AUTH_STORAGE_KEYS.USER_ID, user._id || user.id);
    }
    if (token) {
      localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, token);
    }
    if (userType) {
      localStorage.setItem(AUTH_STORAGE_KEYS.USER_TYPE, userType);
    }
  } catch (e) {
    // localStorage may throw in private browsing
  }

  // 3. Hydrate Redux — single dispatch, sets authLifecycle: AUTHENTICATED
  //    Token is included in the payload — no separate updateAuthToken needed.
  dispatch(setAuthenticatedUser({
    ...user,
    token: token,
    authStatus: true,
    userType: userType,
  }));
}

// ─── Cross-Tab Event Handler ─────────────────────────────────────────────────

/**
 * Handle BroadcastChannel messages from other tabs.
 *
 * Cross-tab logout strategy:
 *   Tab A logs out → clears localStorage (shared) → broadcasts AUTH_LOGOUT.
 *   Tab B receives AUTH_LOGOUT but does NOT immediately:
 *     - Hard redirect
 *     - Clear Redux state
 *     - Destroy UI
 *   Instead, Tab B only cancels any in-flight session restore (prevent race).
 *   Tab B stays visually alive until its next auth-dependent interaction
 *   (API call, navigation, refresh) naturally discovers no valid auth
 *   and transitions cleanly to unauthenticated.
 *
 *   This is intentional:
 *     - No form state disruption
 *     - No user surprise
 *     - No tenant switching confusion
 *     - localStorage is already cleared (shared across tabs)
 *
 * Cross-tab login:
 *   Intentionally NOT implemented. Auto-authenticating Tab B because Tab A
 *   logged in causes form disruption and security surprises.
 *
 * @param dispatch - Redux dispatch function (kept for legacy compat, unused)
 */
export function handleAuthBroadcast(dispatch) {
  return function(event) {
    var message = event.data;

    // Ignore messages from this tab (deduplication)
    if (message?.originTabId === TAB_ID) return;

    if (message?.type === 'AUTH_LOGOUT') {
      // Another tab logged out.
      // localStorage is already cleared (shared storage).
      // Only cancel in-flight restore to prevent race condition
      // where a stale verifyToken response could re-authenticate.
      sessionManager.cancelRestore();

      if (process.env.NODE_ENV === 'development') {
        console.info(
          '[authOrchestrator] Another tab logged out. ' +
          'This tab will detect stale auth on next interaction.',
        );
      }
    }

    // Legacy support: handle old-format messages (empty user objects)
    // from tabs that haven't been refreshed yet during migration
    if (message && !message.type && message.userId === '') {
      sessionManager.cancelRestore();

      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[authOrchestrator] Received legacy BroadcastChannel message. ' +
          'The sending tab should be refreshed to use the new event format.',
        );
      }
    }
  };
}

/**
 * Get the current tab's unique ID.
 * Exposed for testing and debugging.
 */
export function getTabId() {
  return TAB_ID;
}

// ─── Internal Helpers ────────────────────────────────────────────────────────

function clearPrefixedKeys() {
  try {
    var toRemove = [];
    for (var i = 0; i < localStorage.length; i++) {
      var key = localStorage.key(i);
      if (key && PREFIXED_KEYS_TO_CLEAR.some(function(prefix) { return key.startsWith(prefix); })) {
        toRemove.push(key);
      }
    }
    toRemove.forEach(function(k) { localStorage.removeItem(k); });
  } catch (e) {
    // localStorage may throw in private browsing
  }
}
